export type HelpMode = "public_sales" | "signed_in_assistant";

export type HelpRouteKey =
  | "landing"
  | "pricing"
  | "public"
  | "trips"
  | "reports"
  | "manage"
  | "admin"
  | "app";

export interface HelpAction {
  label: string;
  to: string;
}

export interface HelpQuickQuestion {
  id: string;
  label: string;
  type: "content" | "flow" | "pricing_flow";
  targetId: string;
}

export interface HelpStepFlow {
  id: string;
  title: string;
  steps: string[];
  primaryAction: HelpAction;
  secondaryAction?: HelpAction;
}

export interface HelpTeamSizeOption {
  id: string;
  label: string;
  recommendation: string;
  reason: string;
  cta: HelpAction;
}

export interface HelpTeamSizeFlow {
  id: string;
  title: string;
  options: HelpTeamSizeOption[];
}

const question = (value: string) => {
  const trimmed = value.trim();
  return trimmed.endsWith("?") ? trimmed : `${trimmed}?`;
};

const withQuestionLabels = (items: HelpQuickQuestion[]): HelpQuickQuestion[] =>
  items.map((item) => ({ ...item, label: question(item.label) }));

const publicBaseQuestions = withQuestionLabels([
  { id: "public_what_is_service", label: "Mikä tämä palvelu on?", type: "content", targetId: "service_overview" },
  {
    id: "public_why_company",
    label: "Miksi yrityksen kannattaa käyttää tätä?",
    type: "content",
    targetId: "why_company",
  },
  {
    id: "public_why_solo",
    label: "Miksi toiminimiyrittäjän kannattaa käyttää tätä?",
    type: "content",
    targetId: "why_sole_trader",
  },
  {
    id: "public_km_2026",
    label: "Paljonko kilometrikorvaus on vuonna 2026?",
    type: "content",
    targetId: "kilometrikorvaus_2026",
  },
  {
    id: "public_per_diem_2026",
    label: "Paljonko päiväraha on vuonna 2026?",
    type: "content",
    targetId: "paivaraha_2026",
  },
  {
    id: "public_donation",
    label: "Miten lahjoitus nuorten harrastuksiin toimii?",
    type: "content",
    targetId: "donation_mission",
  },
  {
    id: "public_start_buy",
    label: "Miten aloitan ja miten ostan?",
    type: "flow",
    targetId: "start_and_buy_flow",
  },
]);

const pricingLeadQuestion: HelpQuickQuestion = {
  id: "pricing_team_size",
  label: question("Montako käyttäjää teillä on?"),
  type: "pricing_flow",
  targetId: "pricing_team_size_flow",
};

const signedInQuestionsByRoute: Record<"trips" | "reports" | "manage" | "admin" | "app", HelpQuickQuestion[]> = {
  trips: withQuestionLabels([
    {
      id: "trips_new_trip",
      label: "Miten teen uuden matkan?",
      type: "flow",
      targetId: "trips_new_trip_flow",
    },
    {
      id: "trips_required_data",
      label: "Mitä tietoja matkasta pitää tallentaa?",
      type: "flow",
      targetId: "trips_required_data_flow",
    },
    {
      id: "trips_work_private",
      label: "Mikä on työajoa ja mikä yksityisajoa?",
      type: "flow",
      targetId: "trips_work_private_flow",
    },
  ]),
  reports: withQuestionLabels([
    {
      id: "reports_create",
      label: "Miten teen raportin?",
      type: "flow",
      targetId: "reports_create_flow",
    },
    {
      id: "reports_pdf",
      label: "Miten vien raportin PDF:nä?",
      type: "flow",
      targetId: "reports_pdf_flow",
    },
    {
      id: "reports_required",
      label: "Mitä raportissa pitää näkyä?",
      type: "flow",
      targetId: "reports_required_flow",
    },
  ]),
  manage: withQuestionLabels([
    {
      id: "manage_cycle",
      label: "Miten valitsen kuukausi vai vuosi?",
      type: "flow",
      targetId: "manage_cycle_flow",
    },
    {
      id: "manage_user_count",
      label: "Montako käyttäjää organisaatiossa?",
      type: "flow",
      targetId: "manage_user_count_flow",
    },
    {
      id: "manage_add_user",
      label: "Miten lisään käyttäjän?",
      type: "flow",
      targetId: "manage_add_user_flow",
    },
  ]),
  admin: withQuestionLabels([
    {
      id: "admin_partners",
      label: "Mistä löydän kumppanit?",
      type: "flow",
      targetId: "admin_partners_flow",
    },
    {
      id: "admin_payouts",
      label: "Miten tarkistan tilitykset?",
      type: "flow",
      targetId: "admin_payouts_flow",
    },
    {
      id: "admin_back_dashboard",
      label: "Miten palaan dashboardiin?",
      type: "flow",
      targetId: "admin_dashboard_flow",
    },
  ]),
  app: withQuestionLabels([
    {
      id: "app_trips_shortcut",
      label: "Miten teen uuden matkan?",
      type: "flow",
      targetId: "trips_new_trip_flow",
    },
    {
      id: "app_reports_shortcut",
      label: "Miten teen raportin?",
      type: "flow",
      targetId: "reports_create_flow",
    },
    {
      id: "app_manage_shortcut",
      label: "Miten lisään käyttäjän?",
      type: "flow",
      targetId: "manage_add_user_flow",
    },
  ]),
};

const stepFlows: HelpStepFlow[] = [
  {
    id: "start_and_buy_flow",
    title: question("Miten aloitan ja miten ostan?"),
    steps: ["Luo tunnus", "Lisää ensimmäinen matka", "Valitse sopiva tilaus"],
    primaryAction: { label: "Siirry hinnoitteluun", to: "/pricing" },
    secondaryAction: { label: "Luo tili", to: "/register" },
  },
  {
    id: "trips_new_trip_flow",
    title: question("Miten teen uuden matkan?"),
    steps: [
      "Avaa Matkat-näkymä",
      "Paina Lisää matka",
      "Täytä matkan tiedot ja tallenna",
    ],
    primaryAction: { label: "Avaa uusi matka", to: "/trips/new" },
  },
  {
    id: "trips_required_data_flow",
    title: question("Mitä tietoja matkasta pitää tallentaa?"),
    steps: [
      "Lisää matkan tarkoitus ja ajankohta",
      "Kirjaa reitti ja kilometrit",
      "Täydennä tarvittaessa lisätiedot",
    ],
    primaryAction: { label: "Avaa matkat", to: "/trips" },
  },
  {
    id: "trips_work_private_flow",
    title: question("Mikä on työajoa ja mikä yksityisajoa?"),
    steps: [
      "Työajo liittyy työtehtävään",
      "Yksityisajo on omaan käyttöön tehty ajo",
      "Lisää raportille vain työajot",
    ],
    primaryAction: { label: "Avaa ohjeet", to: "/help" },
  },
  {
    id: "reports_create_flow",
    title: question("Miten teen raportin?"),
    steps: [
      "Valitse raporttiin kuuluvat matkat",
      "Luo raportti yhdellä klikkauksella",
      "Tarkista yhteenveto ennen vientiä",
    ],
    primaryAction: { label: "Avaa raportin luonti", to: "/reports?create=1" },
  },
  {
    id: "reports_pdf_flow",
    title: question("Miten vien raportin PDF:nä?"),
    steps: [
      "Avaa valmis raportti listasta",
      "Varmista että tiedot ovat oikein",
      "Lataa PDF tiedosto kirjanpitoon",
    ],
    primaryAction: { label: "Avaa raportit", to: "/reports" },
  },
  {
    id: "reports_required_flow",
    title: question("Mitä raportissa pitää näkyä?"),
    steps: [
      "Näytä matkan tarkoitus ja päivämäärät",
      "Näytä reitti, kilometrit ja korvaukset",
      "Varmista että raportti on helposti tarkastettava",
    ],
    primaryAction: { label: "Avaa raportit", to: "/reports" },
  },
  {
    id: "manage_cycle_flow",
    title: question("Miten valitsen kuukausi vai vuosi?"),
    steps: [
      "Avaa Oma tilaus -välilehti",
      "Vertaa kuukausi- ja vuositilausta",
      "Valitse teille sopivin rytmi",
    ],
    primaryAction: { label: "Avaa tilauksen hallinta", to: "/manage?tab=subscription" },
  },
  {
    id: "manage_user_count_flow",
    title: question("Montako käyttäjää organisaatiossa?"),
    steps: [
      "Avaa Organisaatiot-välilehti",
      "Tarkista nykyinen käyttäjämäärä",
      "Päivitä paikkamäärä tarpeen mukaan",
    ],
    primaryAction: { label: "Avaa organisaatiot", to: "/manage?tab=organizations" },
  },
  {
    id: "manage_add_user_flow",
    title: question("Miten lisään käyttäjän?"),
    steps: [
      "Avaa Organisaatiot-välilehti",
      "Valitse organisaatio ja lisää käyttäjä",
      "Lähetä kutsu sähköpostilla",
    ],
    primaryAction: { label: "Lisää käyttäjä", to: "/manage?tab=organizations" },
  },
  {
    id: "admin_partners_flow",
    title: question("Mistä löydän kumppanit?"),
    steps: [
      "Avaa adminin Kumppanit-näkymä",
      "Hae kumppani listasta",
      "Avaa kumppanin tiedot jatkoa varten",
    ],
    primaryAction: { label: "Avaa kumppanit", to: "/admin/partners" },
  },
  {
    id: "admin_payouts_flow",
    title: question("Miten tarkistan tilitykset?"),
    steps: [
      "Avaa adminin Tilitykset-näkymä",
      "Suodata haluttu ajanjakso",
      "Tarkista maksujen tilanne",
    ],
    primaryAction: { label: "Avaa tilitykset", to: "/admin/partners/payouts" },
  },
  {
    id: "admin_dashboard_flow",
    title: question("Miten palaan dashboardiin?"),
    steps: [
      "Avaa adminin sivupalkki",
      "Valitse Dashboard-linkki",
      "Jatka normaaliin käyttöön",
    ],
    primaryAction: { label: "Avaa dashboard", to: "/dashboard" },
  },
];

const teamSizeFlow: HelpTeamSizeFlow = {
  id: "pricing_team_size_flow",
  title: question("Montako käyttäjää teillä on?"),
  options: [
    {
      id: "team_1",
      label: question("1 käyttäjä?"),
      recommendation: "Suositus: vuositilaus yhdelle käyttäjälle.",
      reason: "Pidät kulut ennakoitavana ja raportointi pysyy selkeänä koko vuoden.",
      cta: { label: "Valitse vuositilaus", to: "/pricing" },
    },
    {
      id: "team_2_5",
      label: question("2–5 käyttäjää?"),
      recommendation: "Suositus: vuositilaus ja organisaation käyttäjähallinta.",
      reason: "Yhteinen malli säästää aikaa, kun kaikkien raportit tehdään samalla tavalla.",
      cta: { label: "Valitse vuositilaus", to: "/pricing" },
    },
    {
      id: "team_6_20",
      label: question("6–20 käyttäjää?"),
      recommendation: "Suositus: organisaatiomalli lisäkäyttäjillä.",
      reason: "Saat raportoinnin yhtenäiseksi ja vähennät manuaalista tarkastusta.",
      cta: { label: "Pyydä tarjous", to: "/company" },
    },
    {
      id: "team_20_plus",
      label: question("Yli 20 käyttäjää?"),
      recommendation: "Suositus: keskustele tiimikohtaisesta käyttöönotosta.",
      reason: "Isoissa tiimeissä selkeä toimintamalli nopeuttaa raportointia merkittävästi.",
      cta: { label: "Pyydä tarjous", to: "/company" },
    },
  ],
};

export const helpFlowsById = stepFlows.reduce<Record<string, HelpStepFlow>>((acc, flow) => {
  acc[flow.id] = flow;
  return acc;
}, {});

export const helpTeamSizeFlowsById: Record<string, HelpTeamSizeFlow> = {
  [teamSizeFlow.id]: teamSizeFlow,
};

export const getHelpMode = (isSignedIn: boolean): HelpMode =>
  isSignedIn ? "signed_in_assistant" : "public_sales";

export const getHelpRouteKey = (pathname: string): HelpRouteKey => {
  if (pathname === "/") return "landing";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/trips")) return "trips";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/manage")) return "manage";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/dashboard") || pathname.startsWith("/settings") || pathname.startsWith("/more") || pathname.startsWith("/help")) {
    return "app";
  }
  if (
    pathname.startsWith("/company") ||
    pathname.startsWith("/privacy") ||
    pathname.startsWith("/ukk") ||
    pathname.startsWith("/ohjeet") ||
    pathname.startsWith("/hyvantekevaisyys") ||
    pathname.startsWith("/seurat") ||
    pathname.startsWith("/verkkolasku")
  ) {
    return "public";
  }
  return "public";
};

export const getQuickQuestions = (mode: HelpMode, routeKey: HelpRouteKey): HelpQuickQuestion[] => {
  if (mode === "public_sales") {
    if (routeKey === "pricing") {
      return [pricingLeadQuestion, ...publicBaseQuestions];
    }
    return publicBaseQuestions;
  }

  if (routeKey === "trips" || routeKey === "reports" || routeKey === "manage" || routeKey === "admin" || routeKey === "app") {
    return signedInQuestionsByRoute[routeKey];
  }

  return signedInQuestionsByRoute.app;
};
